import { defineStore } from 'pinia'
import axios from 'axios'
import { supabase } from '@/supabase'
import { watch } from 'vue'

const imdbAPIKey = 'b7eabdf1451cb83f62a70c7c723dd02f'
const tdmbBase = 'https://api.themoviedb.org/3'

const axMovie = axios.create({
  baseURL: tdmbBase,
  timeout: 1000,
  withCredentials: false
})
const searchAdult = false
const searchLang = 'en-US'

async function tmdbApiSearch(type, query, options = {}) {
  const params = new URLSearchParams({
    api_key: imdbAPIKey,
    query: query,
    language: options.language || searchLang,
    include_adult:
      options.includeAdult !== undefined ? options.includeAdult : searchAdult,
    page: options.page || 1
  })

  // Add type-specific parameters
  if (type === 'movie') {
    if (options.year) {
      params.append('year', options.year)
    }
    if (options.primary_release_year) {
      params.append('primary_release_year', options.primary_release_year)
    }
    if (options.region) {
      params.append('region', options.region)
    }
  } else if (type === 'tv') {
    if (options.first_air_date_year) {
      params.append('first_air_date_year', options.first_air_date_year)
    }
  }
  // Person search doesn't have additional parameters beyond the base ones

  return axMovie.get(`search/${type}?${params.toString()}`).then((response) => {
    return response.data
  })
}

async function tmdbApiDiscover(type, query) {
  return axMovie
    .get(
      `discover/${type}?api_key=${imdbAPIKey}&with_keywords=${query}&language=${searchLang}&include_adult=${searchAdult}&page=1`
    )
    .then((response) => {
      return response.data
    })
}

export const useStoreTMDB = defineStore('tmdbStore', {
  persist: false,
  state: () => ({
    titles: [],
    titlesLoading: false,
    episodes: [],
    episodesLoading: false,
    persons: [],
    personsLoading: false,
    collections: [],
    collectionsLoading: false,
    titleFetchError: null,
    titleRecords: [],
    trendingTitles: [], // Add trendingTitles to the state
    trendingTitlesLoading: false,
    searchResults: {},
    searchQuery: '',
    searchResultsLoading: false,
    searchQueryHistory: [],

    watchProvidersRegions: [],
    watchProvidersRegionsLoading: false,
    watchProvidersMovies: [],
    watchProvidersMoviesLoading: false
  }),
  actions: {
    init() {},

    makeSearchKey(type, query, options = {}) {
      // Create a key that includes type, query, and filter options
      const keyParts = [type, query]
      if (options.year) keyParts.push(`year:${options.year}`)
      if (options.primary_release_year)
        keyParts.push(`pryear:${options.primary_release_year}`)
      if (options.first_air_date_year)
        keyParts.push(`fadyear:${options.first_air_date_year}`)
      if (options.region) keyParts.push(`region:${options.region}`)
      if (options.language) keyParts.push(`lang:${options.language}`)
      if (options.includeAdult !== undefined)
        keyParts.push(`adult:${options.includeAdult}`)
      return btoa(unescape(encodeURIComponent(keyParts.join('-'))))
    },

    storeSearchResults(type, query, data, options = {}) {
      // Create an object with a key that's the combo of the type + query + filters and store the data
      // in the search results object, along with the query, type, options, and timestamp of the search
      const key = this.makeSearchKey(type, query, options)
      this.searchResults[key] = {
        data: data,
        query: query,
        type: type,
        options: options,
        timestamp: Date.now(),
        // Pagination info from TMDB response
        page: data.page || 1,
        totalPages: data.total_pages || 1,
        totalResults: data.total_results || 0,
        loadingMore: false
      }

      return key
    },

    async fetchSearchResults(type, query, options = {}) {
      // Check if we have a search result for this query with these options already
      const key = this.makeSearchKey(type, query, options)
      const existing = this.searchResults[key]
      if (existing) {
        return existing
      }

      const res = await tmdbApiSearch(type, query, options)
      this.storeSearchResults(type, query, res, options)
      return res
    },

    async fetchMoreSearchResults(type, query, options = {}) {
      const key = this.makeSearchKey(type, query, options)
      const existing = this.searchResults[key]

      if (!existing) {
        // No existing results, fetch first page
        return this.fetchSearchResults(type, query, options)
      }

      // Check if we have more pages to load
      if (existing.page >= existing.totalPages) {
        return existing
      }

      // Prevent duplicate requests
      if (existing.loadingMore) {
        return existing
      }

      // Mark as loading
      existing.loadingMore = true

      try {
        const nextPage = existing.page + 1
        const res = await tmdbApiSearch(type, query, { ...options, page: nextPage })

        // Append new results to existing
        existing.data.results = [...existing.data.results, ...res.results]
        existing.page = res.page
        existing.totalPages = res.total_pages
        existing.totalResults = res.total_results
        existing.loadingMore = false

        return existing
      } catch (error) {
        existing.loadingMore = false
        throw error
      }
    },

    async fetchTrendingTitles() {
      this.trendingTitlesLoading = true
      try {
        const response = await axios.get(
          `${tdmbBase}/trending/movie/day?api_key=${imdbAPIKey}`,
          { withCredentials: false }
        )
        this.trendingTitles = response.data.results
        this.trendingTitlesLoading = false
        return response.data
      } catch (error) {
        this.trendingTitlesLoading = false
        console.error('Error fetching trending titles:', error)
        throw error
      }
    },

    fetchCollectionById(id) {
      const existing = this.collections.find((obj) => obj.id == id)
      if (existing) {
        return existing
      }
      this.collectionsLoading = true

      return axMovie
        .get(`/collection/${id}?api_key=${imdbAPIKey}`)
        .then((response) => {
          const data = response.data
          this.collectionsLoading = false
          this.collections.push(data)
          return data
        })
        .catch((err) => {
          return err
        })
    },

    async fetchTitleTVEpisodesById(id, season_number, force = false) {
      const existing = this.episodes.find(
        (obj) => obj.id == id && obj.season_number == season_number
      )
      if (existing) {
        return existing
      }

      // Make a stub item to prevent multiple requests
      this.episodes.push({
        id: id,
        season_number: season_number,
        fetched: Date.now(),
        data: null,
        loading: true
      })

      this.episodesLoading = true

      let appendToResponse = ''

      await axMovie
        .get(
          `/tv/${id}/season/${season_number}?api_key=${imdbAPIKey}&append_to_response=${appendToResponse}`,
          { withCredentials: false }
        )
        .then((response) => {
          const data = response.data
          this.episodesLoading = false

          // Find the stub item and replace it with the real data
          const obj = {
            id: id,
            season_number: season_number,
            fetched: Date.now(),
            data: data
          }

          const index = this.episodes.findIndex(
            (obj) => obj.id == id && obj.season_number == season_number
          )
          this.episodes[index] = obj

          return data
        })
        .catch((err) => {
          console.log('error fetching episodes', err)
          return err
        })
    },

    async fetchTitleById(id, type) {
      const existing = this.titles.find((obj) => obj.id == id)
      if (existing) {
        return existing
      }

      // Make a stub item to prevent multiple requests
      this.titles.push({
        id: id,
        type: type,
        fetched: Date.now(),
        data: null,
        loading: true
      })

      this.titlesLoading = true

      let appendToResponse =
        'credits,external_ids,images,keywords,recommendations,release_dates,videos,watch/providers'
      if (type === 'tv') {
        // Append tv specific data
        appendToResponse =
          'aggregate_credits,external_ids,images,keywords,recommendations,release_dates,videos,watch/providers,season/1'
      } else if (type === 'movie') {
        // append movie specific data..
      } else if (type === 'person') {
        appendToResponse = 'combined_credits,external_ids,images,tagged_images'
      }

      await axMovie
        .get(
          `/${type}/${id}?api_key=${imdbAPIKey}&append_to_response=${appendToResponse}`,
          { withCredentials: false }
        )
        .then((response) => {
          const data = response.data
          this.titlesLoading = false

          const obj = {
            id: data.id,
            type: type,
            fetched: Date.now(),
            data: data
          }

          const index = this.titles.findIndex(
            (obj) => obj.id == id && obj.type == type
          )
          this.titles[index] = obj

          return data
        })
        .catch((err) => {
          return err
        })
    },

    async fetchWatchProvidersRegions() {
      this.watchProvidersRegionsLoading = true
      return axMovie
        .get(`/watch/providers/regions?api_key=${imdbAPIKey}`)
        .then((response) => {
          this.watchProvidersRegions = response.data.results

          return response.data
        })
        .catch((err) => {
          return err
        })
        .finally(() => {
          this.watchProvidersRegionsLoading = false
        })
    },

    async fetchWatchProvidersMovies() {
      this.watchProvidersMoviesLoading = true
      return axMovie
        .get(`/watch/providers/movie?api_key=${imdbAPIKey}`)
        .then((response) => {
          this.watchProvidersMovies = response.data.results

          return response.data
        })
        .catch((err) => {
          return err
        })
        .finally(() => {
          this.watchProvidersMoviesLoading = false
        })
    }
  },
  getters: {
    // watchProvidersRegions: (state) => {
    //   return state.watchProvidersRegions
    // },
    // watchProvidersMovies: (state) => {
    //   return state.watchProvidersMovies
    // },

    getSearchResultsByKey: (state) => {
      return (key) => {
        return state.searchResults[key]
      }
    },

    getTitleById: (state) => {
      return (id, type) => {
        const title = state.titles.find((title) => title.id == id)

        // Append media_type because the API does not return it
        const data = title?.data
        if (data) {
          data.media_type = type
        }
        return data
      }
    },

    getEpisodesByIdAndSeason: (state) => {
      return (id, season_number) => {
        const episode = state.episodes.find(
          (episode) =>
            episode.id == id && episode.season_number == season_number
        )

        return episode
      }
    },
    getPersonById: (state) => {
      return (id) => {
        const person = state.titles.find(
          (title) => title.id == id && title.type === 'person'
        )

        return person?.data
      }
    }
  }
})
